import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
} from "@mui/material";
import useAdminReturns from "../../../hooks/useAdminReturns";

/**
 * Mark Items Received Form component for admin interface
 * Allows admins to mark returned items as received, specify conditions and quantities
 *
 * @param {Object} props - Component props
 * @param {Object} props.returnDetail - The return being processed
 * @returns {JSX.Element} The MarkItemsReceivedForm component
 */
const MarkItemsReceivedForm = ({ returnDetail }) => {
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("returns");
  const { markAsReceived, actionStatus, actionError } = useAdminReturns();

  // Get only approved items
  const approvedItems =
    returnDetail?.items?.filter((item) => item.adminDecision === "approved") ||
    [];

  // Initialize form data with receipts for each approved return item
  const [formData, setFormData] = useState({
    itemReceipts: approvedItems.map((item) => ({
      returnItemId: item.id,
      receivedQuantity: item.adminQuantity,
      condition: item.condition || "used", // Default to used if not specified
      adminNotes: "",
    })),
    globalAdminNotes: "",
  });

  const [formErrors, setFormErrors] = useState({});

  const handleItemReceiptChange = (itemIndex, field, value) => {
    setFormData((prevData) => {
      const updatedReceipts = [...prevData.itemReceipts];
      updatedReceipts[itemIndex] = {
        ...updatedReceipts[itemIndex],
        [field]: value,
      };

      return {
        ...prevData,
        itemReceipts: updatedReceipts,
      };
    });
  };

  const handleNotesChange = (e) => {
    setFormData((prevData) => ({
      ...prevData,
      globalAdminNotes: e.target.value,
    }));
  };

  const validateForm = () => {
    const errors = {};

    // Check if at least one item is being received
    if (!formData.itemReceipts || formData.itemReceipts.length === 0) {
      errors.general = t("admin.detail.forms.markReceived.errors.noItems");
    }

    // Check individual item receipts
    formData.itemReceipts.forEach((receipt, index) => {
      const qty = parseInt(receipt.receivedQuantity, 10);
      const maxQty = approvedItems[index].adminQuantity;

      if (isNaN(qty) || qty < 0 || qty > maxQty) {
        errors[`item_${index}_quantity`] = t(
          "admin.detail.forms.markReceived.errors.quantityRange",
          { maxQty }
        );
      }

      if (!receipt.condition) {
        errors[`item_${index}_condition`] = t(
          "admin.detail.forms.markReceived.errors.conditionRequired"
        );
      }
    });

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    markAsReceived({
      returnId: returnDetail.id,
      itemReceipts: formData.itemReceipts,
      globalAdminNotes: formData.globalAdminNotes,
    });
  };

  const isLoading = actionStatus === "loading";

  if (!approvedItems || approvedItems.length === 0) {
    return (
      <Alert severity="warning">
        {t("admin.detail.forms.markReceived.noApprovedItems")}
      </Alert>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {actionError?.message || tCommon("status.error")}
        </Alert>
      )}

      {formErrors.general && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {formErrors.general}
        </Alert>
      )}

      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                {t("admin.detail.forms.markReceived.table.product")}
              </TableCell>
              <TableCell align="center">
                {t("admin.detail.forms.markReceived.table.approvedQty")}
              </TableCell>
              <TableCell align="center">
                {t("admin.detail.forms.markReceived.table.receivedQty")}
              </TableCell>
              <TableCell>
                {t("admin.detail.forms.markReceived.table.condition")}
              </TableCell>
              <TableCell>
                {t("admin.detail.forms.markReceived.table.notes")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {approvedItems.map((item, index) => (
              <TableRow key={item.id}>
                <TableCell>
                  {item.orderItem?.product?.name || "N/A"}
                  <Typography variant="caption" display="block">
                    {t("admin.detail.forms.markReceived.table.itemId", {
                      id: item.id.substring(0, 8),
                    })}
                  </Typography>
                </TableCell>
                <TableCell align="center">{item.adminQuantity}</TableCell>
                <TableCell align="center">
                  <TextField
                    type="number"
                    size="small"
                    inputProps={{
                      min: 0,
                      max: item.adminQuantity,
                      style: { textAlign: "center" },
                    }}
                    value={formData.itemReceipts[index].receivedQuantity}
                    onChange={(e) =>
                      handleItemReceiptChange(
                        index,
                        "receivedQuantity",
                        parseInt(e.target.value, 10)
                      )
                    }
                    error={!!formErrors[`item_${index}_quantity`]}
                    helperText={formErrors[`item_${index}_quantity`]}
                    sx={{ width: "80px" }}
                  />
                </TableCell>
                <TableCell>
                  <FormControl
                    size="small"
                    fullWidth
                    error={!!formErrors[`item_${index}_condition`]}
                  >
                    <Select
                      value={formData.itemReceipts[index].condition}
                      onChange={(e) =>
                        handleItemReceiptChange(
                          index,
                          "condition",
                          e.target.value
                        )
                      }
                    >
                      <MenuItem value="unused">
                        {t("admin.detail.forms.markReceived.conditions.unused")}
                      </MenuItem>
                      <MenuItem value="used">
                        {t("admin.detail.forms.markReceived.conditions.used")}
                      </MenuItem>
                      <MenuItem value="damaged">
                        {t(
                          "admin.detail.forms.markReceived.conditions.damaged"
                        )}
                      </MenuItem>
                      <MenuItem value="other">
                        {t("admin.detail.forms.markReceived.conditions.other")}
                      </MenuItem>
                    </Select>
                    {formErrors[`item_${index}_condition`] && (
                      <FormHelperText>
                        {formErrors[`item_${index}_condition`]}
                      </FormHelperText>
                    )}
                  </FormControl>
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder={t(
                      "admin.detail.forms.markReceived.itemNotesPlaceholder"
                    )}
                    value={formData.itemReceipts[index].adminNotes}
                    onChange={(e) =>
                      handleItemReceiptChange(
                        index,
                        "adminNotes",
                        e.target.value
                      )
                    }
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            name="globalAdminNotes"
            label={t("admin.detail.forms.markReceived.globalNotesLabel")}
            multiline
            rows={3}
            value={formData.globalAdminNotes}
            onChange={handleNotesChange}
            fullWidth
            placeholder={t(
              "admin.detail.forms.markReceived.globalNotesPlaceholder"
            )}
          />
        </Grid>

        <Grid item xs={12} sx={{ mt: 1 }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={isLoading}
            sx={{ mr: 1 }}
          >
            {isLoading ? (
              <>
                <CircularProgress size={24} sx={{ mr: 1 }} />
                {tCommon("status.processing")}
              </>
            ) : (
              t("admin.detail.forms.markReceived.submitButton")
            )}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MarkItemsReceivedForm;
